// src/views/knowledge-base/steps-utils.ts
// Shared display transforms for KB tutorial steps.
// Used by WalkthroughPlayer (player) and KnowledgeBasePanel (read-only list)
// so the user sees the same step sequence in both places.
import type { KbStep } from './types'

const NAV_RE = /^\s*Navigate to\s+\S+/i
const ACTION_RE = /^\s*(Click|Type|Navigate|Press|Select|Choose|Hover|Drag|Open|Close|Enter|Submit)\b/i

type ActionKind = 'click' | 'type' | 'navigate' | 'other'

function deriveKind(step: KbStep): ActionKind {
  const m = step.text.match(ACTION_RE)
  if (m) {
    const lower = m[1].toLowerCase()
    if (lower === 'click' || lower === 'press' || lower === 'select' || lower === 'choose' || lower === 'hover' || lower === 'open' || lower === 'close') return 'click'
    if (lower === 'type' || lower === 'enter' || lower === 'submit') return 'type'
    if (lower === 'navigate') return 'navigate'
    return 'other'
  }
  const tag = (step.tag || '').toUpperCase()
  if (tag === 'INPUT' || tag === 'TEXTAREA') return 'type'
  if (tag === 'BUTTON' || tag === 'A' || tag === 'SVG') return 'click'
  return 'other'
}

/**
 * Merge consecutive Click+Type pairs targeting the same selector/xpath when the
 * next step's tag is INPUT/TEXTAREA. Keeps the Type step, drops the Click.
 */
export function coalesceSteps(steps: KbStep[]): KbStep[] {
  const out: KbStep[] = []
  let i = 0
  while (i < steps.length) {
    const a = steps[i]
    const b = steps[i + 1]
    if (a && b) {
      const aKind = deriveKind(a)
      const bKind = deriveKind(b)
      const bTag = (b.tag || '').toUpperCase()
      const sameTarget = (a.selector && b.selector && a.selector === b.selector)
        || (!a.selector && !b.selector && a.xpath && b.xpath && a.xpath === b.xpath)
      if (aKind === 'click' && bKind === 'type' && sameTarget && (bTag === 'INPUT' || bTag === 'TEXTAREA')) {
        out.push({ ...b })
        i += 2
        continue
      }
    }
    out.push(a)
    i++
  }
  return out
}

/** Drop leading "Navigate to ..." steps. */
export function stripLeadingNavigateSteps(steps: KbStep[]): KbStep[] {
  let i = 0
  while (i < steps.length && NAV_RE.test(steps[i].text || '')) i++
  return steps.slice(i)
}

/** Canonical "what users see" transform. */
export function displaySteps(steps: KbStep[]): KbStep[] {
  return coalesceSteps(stripLeadingNavigateSteps(steps))
}
